import { Products } from '@/comm'
import { useDatePicker } from '@/components/MyDatePicker'
import dayjs from 'dayjs'
import { useMemo } from 'react'
import { getDateRange } from '@/lib/date-utils'

import { Button } from '@/components/ui/button'
import { createColumnVisibilityStore } from '@/components/zthd/data-table'
import { DataTableViewOptions } from '@/components/zthd/data-table-view-options'
import DataTableWithBorder from '@/components/zthd/data-table/data-table-with-border'
import {
  getCoreRowModel,
  getFilteredRowModel,
  useReactTable,
} from '@tanstack/react-table'
import {
  useColumnFilters,
  VSWeekSelector,
} from '@/components/data-table-selectors'
import { useCountry } from '@/components/DropdownCountry'
import { PID_GROUP } from '@/comm'
import useSWR from 'swr'
import { columnDate, summaryGroupColumn } from './columns'

import * as React from 'react'
import { usePid2 } from '@/components/DropdownPid'

const initialColumnVisibility = Products.filter(
  ({ code }) => !['22', '23'].includes(code),
).reduce((acc, p) => ({ ...acc, [p.code]: true }), {})

const useColumnVisibilityStore = createColumnVisibilityStore(
  initialColumnVisibility,
  'pcs',
)

const convertPids = (pids) => {
  return [pids.map((pid) => PID_GROUP[pid]).flat(), pids].flat()
}

const Home = () => {
  const startDate = useDatePicker((state) => state.startDate)
  const endDate = useDatePicker((state) => state.endDate)
  const country = useCountry((state) => state.value)
  const pids = usePid2((state) => state.pids)

  const { columnVisibility, setColumnVisibility } = useColumnVisibilityStore(
    (state) => state,
  )
  const columnFilters = useColumnFilters((state) => state.columnFilters)

  const { data: revenues, mutate: mutateR } = useSWR([
    '/revenues/pc',
    { startDate, endDate, country },
  ])

  const { data: costs, mutate: mutateC } = useSWR([
    '/costs/pc',
    { startDate, endDate, country },
  ])

  const { data: drs, mutate: mutateD } = useSWR([
    '/drs/pc',
    { startDate, endDate, country },
  ])

  const data = useMemo(() => {
    const dateRange = getDateRange(dayjs(startDate), dayjs(endDate))

    const data = dateRange.map((date) => ({
      date,
      costs: costs?.[date],
      revenues: revenues?.[date],
      drs: drs?.[date],
    }))
    return data
  }, [startDate, endDate, costs, revenues, drs])

  const columns = useMemo(() => {
    const ps = Products.filter(
      ({ code }) => !['22', '23'].includes(code),
    ).filter(
      ({ code }) => pids.includes('total') || convertPids(pids).includes(code),
    )
    const rows = [columnDate]

    ps.forEach((p, index) => {
      const { code, name } = p
      rows.push(summaryGroupColumn(code, name, index % 2))
    })
    return rows
  }, [pids])

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    enableMultiRowSelection: true,
    state: {
      columnFilters,
      columnVisibility,
    },
  })

  const refresh = () => {
    mutateR({ revalidate: false })
    mutateC({ revalidate: false })
    mutateD({ revalidate: false })
  }

  return (
    <>
      <div className="flex items-center gap-1 py-4">
        <VSWeekSelector />
        <Button onClick={refresh}>refresh</Button>
        <DataTableViewOptions table={table} />
      </div>
      <DataTableWithBorder table={table} />
    </>
  )
}

export default Home
